import { useState } from 'react'
import { applyForScholarship } from '../lib/content'
import { COURSES, FALLBACK_OFFERS, formatSessionDate, formatDuration, type LiveOffers } from '../config/cta'

// Scholarship application landing + form for the AI/agents Maven cohort.
// A few comped "founding cohort" seats, awarded by application, in exchange for
// showing up + a testimonial/case study.
//
// CLOSED: that cohort has run, so the round is over and the form is retired. The
// page stays up (the URL is linked from past emails and posts) but serves an
// archived notice that sends readers to whatever is live now. To re-run this for
// a future cohort: set OPEN back to true and update the constants below — seats,
// deadline, first session, and the course it points at.
const OPEN = false
const COURSE = COURSES.aiAgents
const SEATS = 3
const DEADLINE_LABEL = 'Sunday 12 July, 11:59pm London'
const FIRST_SESSION_LABEL = 'Tuesday 14 July'

const WHAT_YOU_GET = [
  'A full seat in the six-week cohort: twelve live sessions and five graded projects, same as every paying student.',
  'Direct feedback on the LLM and agent systems you ship, from someone who has run them in production.',
  'A cohort of engineers building the same thing at the same time, not a course you watch alone.',
]

const WHAT_WE_ASK = [
  `Show up live. Sessions run Tuesdays and Thursdays, 7:30–9pm London, starting ${FIRST_SESSION_LABEL}.`,
  'Do the projects. The value is in shipping, not watching.',
  'At the end, give a short testimonial and let me write up your project as a case study.',
]

export default function ScholarshipPage({ offers = FALLBACK_OFFERS }: { offers?: LiveOffers }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('')
  const [company, setCompany] = useState('')
  const [link, setLink] = useState('')
  const [building, setBuilding] = useState('')
  const [why, setWhy] = useState('')
  const [commit, setCommit] = useState(false)
  // Honeypot. Never shown, never labelled, never focusable. A person cannot fill
  // it; a bot that fills every input in the DOM will. The backend discards any
  // submission that has it set.
  const [website, setWebsite] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [error, setError] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!commit) {
      setError('Please confirm you can commit to the sessions and the testimonial.')
      return
    }
    setStatus('loading')
    try {
      await applyForScholarship({
        course: COURSE.id,
        name: name.trim(),
        email: email.trim(),
        role: role.trim(),
        company: company.trim() || undefined,
        link: link.trim() || undefined,
        building: building.trim(),
        why: why.trim(),
        commit,
        website,
      })
      setStatus('done')
    } catch (err) {
      setError((err as Error).message)
      setStatus('error')
    }
  }

  const inputCls =
    'w-full rounded-smooth border border-border-strong bg-white px-4 py-3 font-body text-body text-brand-navy focus:outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/20'
  const labelCls = 'block font-heading text-overline uppercase tracking-wider text-ink-soft mb-1.5'

  // Archived round: say so plainly at the top, then hand the reader the two
  // things they can still act on rather than leaving them on a dead end.
  if (!OPEN) {
    return (
      <div className="bg-brand-cream">
        <section className="max-w-3xl mx-auto px-4 pt-16 pb-16 md:pt-24 md:pb-24">
          <p className="font-heading text-overline uppercase tracking-wider text-ink-muted mb-4">
            Applications closed
          </p>
          <h1 className="font-heading text-h2 md:text-4xl font-bold text-brand-navy leading-[1.15] mb-5">
            The {COURSE.title} scholarship round has ended.
          </h1>
          <p className="font-body text-lg leading-[1.6] text-ink-soft mb-4">
            {SEATS} seats were awarded for that cohort and it has since run, so this application is
            no longer open. Thank you to everyone who applied: I read every one.
          </p>
          <p className="font-body text-body text-ink-muted mb-10">
            I run scholarship rounds when a new cohort is announced. The best way to hear about the
            next one is to come to a free live session, where I mention them first.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Only offer the session when one is actually scheduled. */}
            {offers.lesson ? (
              <a
                href={offers.lesson.url}
                target="_blank"
                rel="noreferrer"
                className="block bg-brand-navy text-white rounded-smooth-lg p-6 hover:opacity-95 transition-opacity"
              >
                <p className="font-heading text-overline uppercase tracking-wider text-brand-gold mb-3">
                  Next free live session
                </p>
                {/* text-white is explicit: a global rule colours h2 brand-navy,
                    which is invisible against this card. */}
                <h2 className="font-heading text-lg font-bold text-white leading-snug mb-2">{offers.lesson.title}</h2>
                {formatSessionDate(offers.lesson.startsAt) ? (
                  <p className="font-body text-body-sm text-white/70 mb-4">
                    {formatSessionDate(offers.lesson.startsAt)}
                    {formatDuration(offers.lesson.durationMins) ? ` · ${formatDuration(offers.lesson.durationMins)}` : ''}
                  </p>
                ) : null}
                <span className="font-body text-body-sm text-white underline underline-offset-2">
                  Sign up free on Maven →
                </span>
              </a>
            ) : null}
            <a
              href={offers.course.url}
              target="_blank"
              rel="noreferrer"
              className="block bg-white border border-border-subtle rounded-smooth-lg p-6 hover:border-brand-navy/40 transition-colors"
            >
              <p className="font-heading text-overline uppercase tracking-wider text-ink-muted mb-3">
                Go deeper
              </p>
              <h2 className="font-heading text-lg font-bold text-brand-navy leading-snug mb-2">
                {offers.course.title}
              </h2>
              <p className="font-body text-body-sm text-ink-soft mb-4">{offers.course.blurb}</p>
              <span className="font-body text-body-sm text-brand-navy font-semibold underline underline-offset-2">
                See the syllabus on Maven →
              </span>
            </a>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="bg-brand-cream">
      {/* Hero */}
      <section className="max-w-3xl mx-auto px-4 pt-16 pb-10 md:pt-24 md:pb-14">
        <p className="font-heading text-overline uppercase tracking-wider text-brand-red mb-4">
          {SEATS} scholarship seats · apply by {DEADLINE_LABEL}
        </p>
        <h1 className="font-heading text-h2 md:text-4xl font-bold text-brand-navy leading-[1.15] mb-5">
          Scholarship seats: {COURSE.title}
        </h1>
        <p className="font-body text-lg leading-[1.6] text-ink-soft mb-4">
          I&rsquo;m opening {SEATS} scholarship seats in this cohort for engineers who&rsquo;d get
          real value from it but for whom the price is a genuine barrier. They&rsquo;re awarded by
          application, and I read every one.
        </p>
        <p className="font-body text-body text-ink-muted">
          The cohort starts {FIRST_SESSION_LABEL}, so applications close{' '}
          <span className="text-brand-navy font-semibold">{DEADLINE_LABEL}</span> and I&rsquo;ll
          reply to everyone the next day.
        </p>
      </section>

      {/* About the cohort — what the scholarship gets you into */}
      <section className="max-w-3xl mx-auto px-4 pb-4">
        <div className="bg-brand-navy text-white rounded-smooth-lg p-6 md:p-8">
          <p className="font-heading text-overline uppercase tracking-wider text-white/60 mb-3">
            About the cohort
          </p>
          <p className="font-body text-lg leading-[1.6] text-white/95 mb-4">{COURSE.blurb}</p>
          <ul className="grid sm:grid-cols-3 gap-3 mb-5">
            {['6 weeks', '12 live sessions', '5 graded projects'].map((t) => (
              <li key={t} className="bg-white/10 rounded-smooth px-4 py-3 font-heading font-bold text-center">
                {t}
              </li>
            ))}
          </ul>
          <a
            href={COURSE.url}
            target="_blank"
            rel="noreferrer"
            className="font-body text-body-sm text-white underline underline-offset-2 hover:text-white/80"
          >
            See the full syllabus and schedule on Maven →
          </a>
        </div>
      </section>

      {/* What you get / what I ask */}
      <section className="max-w-3xl mx-auto px-4 pb-4">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-border-subtle rounded-smooth-lg p-6">
            <h2 className="font-heading text-lg font-bold text-brand-navy mb-4">What you get</h2>
            <ul className="space-y-3">
              {WHAT_YOU_GET.map((t) => (
                <li key={t} className="flex gap-3 font-body text-body-sm text-ink-soft leading-[1.5]">
                  <span className="text-brand-red font-bold flex-shrink-0">→</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-border-subtle rounded-smooth-lg p-6">
            <h2 className="font-heading text-lg font-bold text-brand-navy mb-4">What I ask in return</h2>
            <ul className="space-y-3">
              {WHAT_WE_ASK.map((t) => (
                <li key={t} className="flex gap-3 font-body text-body-sm text-ink-soft leading-[1.5]">
                  <span className="text-brand-red font-bold flex-shrink-0">→</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="max-w-3xl mx-auto px-4 py-10 md:py-14">
        {status === 'done' ? (
          <div className="bg-white border border-border-subtle rounded-smooth-lg p-8 md:p-10 text-center">
            <h2 className="font-heading text-h2 font-bold text-brand-navy mb-3">Application received.</h2>
            <p className="font-body text-body text-ink-soft max-w-md mx-auto">
              Thanks for applying. I read every application personally and will email you by the day
              after the deadline, {DEADLINE_LABEL.split(',')[0]}, either way. Keep an eye on your inbox.
            </p>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-h2 font-bold text-brand-navy mb-2">Apply for a seat</h2>
            <p className="font-body text-body-sm text-ink-muted mb-8">
              A few honest sentences beat a polished essay. This takes about five minutes.
            </p>
            <form onSubmit={submit} className="space-y-5 bg-white border border-border-subtle rounded-smooth-lg p-6 md:p-8">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className={inputCls} />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Current role / title</label>
                  <input type="text" value={role} onChange={(e) => setRole(e.target.value)} required placeholder="e.g. Senior Backend Engineer" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Company <span className="normal-case text-ink-muted/70">(optional)</span></label>
                  <input type="text" value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label className={labelCls}>LinkedIn / GitHub / portfolio <span className="normal-case text-ink-muted/70">(optional)</span></label>
                <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" className={inputCls} />
              </div>
              {/*
                Honeypot. Positioned off-screen rather than display:none, because
                some bots skip hidden inputs but not absolutely-positioned ones.
                aria-hidden and tabIndex={-1} keep it away from screen readers
                and the tab order, so it is invisible to people using the form.
              */}
              <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <div>
                <label className={labelCls}>What are you building, or want to build, with LLMs and agents?</label>
                <textarea value={building} onChange={(e) => setBuilding(e.target.value)} required rows={4} maxLength={2000} className={`${inputCls} resize-y`} />
              </div>
              <div>
                <label className={labelCls}>Why this cohort, and why now?</label>
                <textarea value={why} onChange={(e) => setWhy(e.target.value)} required rows={4} maxLength={2000} className={`${inputCls} resize-y`} />
              </div>

              <label className="flex items-start gap-3 font-body text-body-sm text-ink-soft cursor-pointer">
                <input type="checkbox" checked={commit} onChange={(e) => setCommit(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0" />
                <span>
                  If selected, I can attend the live sessions ({FIRST_SESSION_LABEL} onward, Tue &amp; Thu
                  7:30&ndash;9pm London), and I&rsquo;ll give a short testimonial and let my project be written
                  up as a case study.
                </span>
              </label>

              {error ? <p className="font-body text-body-sm text-brand-red">{error}</p> : null}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-brand-navy text-white font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {status === 'loading' ? 'Sending…' : 'Submit application'}
              </button>
              <p className="font-body text-caption text-ink-muted text-center">
                Prefer to pay your way in?{' '}
                <a href={COURSE.url} target="_blank" rel="noreferrer" className="text-brand-navy font-semibold underline">
                  Enroll directly on Maven
                </a>
                .
              </p>
            </form>
          </>
        )}
      </section>
    </div>
  )
}
